import {
	FC,
	MouseEventHandler,
	ReactNode,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { Style } from '@capacitor/status-bar';
import { IconButton, Typography } from '@mui/material';

import {
	useDTicketSubscriptionOffering,
	useUser,
	useUserActions,
} from '@mopla/business-logic';
import {
	DTicketProfileManagerProps,
	IWhoami,
	ProfileSteps,
	TPaymentType,
	TSubscription,
} from '@mopla/data-models';
import { getDTicketType, useBreakpoints, useI18nLanguage } from '@mopla/utils';

import {
	EPassengerRouteParams,
	MplButton,
	MplErrorModal,
	MplPageHeader,
	useStatusBarStyleSync,
} from '../../';
import { Loader } from '../../atoms/loader/Loader';
import { EditIcon, TrashIconSmall } from '../../icons';
import { BottomSlider } from '../../molecules/BottomSlider/BottomSlider';
import { useMoplaRideTypesControl } from '../MoplaRideTypesDrawer';
import { useDiscardChangesPrompt } from '../prompt/useDiscardChangesPrompt';
import SideOverlay from '../SideOverlay/SideOverlay';

import { ProfileHeader } from './ProfileHeader/ProfileHeader';
import { ClosePersonalEditorModal } from './ClosePersonalEditorModal';
import { useProfileManagerContext } from './context';
import { DriverFeedbackProvider, DriverReviewPage } from './DriverReviewPage';
import { EditUserScreen } from './EditUserScreen';
import { EditEmailScreen, VerifyEditEmailScreen } from './EmailEdit';
import { FAQScreen } from './FAQScreen';
import { InitialProfileScreen } from './InititalScreen';
import { LanguageSelectScreen } from './LanguageSelect';
import { PersonalScreen } from './PersonalScreen';
import {
	DesktopDataBlock,
	DesktopDataTitle,
	DesktopRoot,
	ImprintButton,
	MyAccountTitleWithButton,
	Root,
} from './ProfileManager.styles';
import { withProfileManagerContext } from './withProfileManagerContext';

type personalDetails = {
	firstName: string;
	lastName: string;
	phone: string;
	dateOfBirth: string | null;
	streetNumber: string | null;
	zipcode: string | null;
	city: string | null;
};

export type TEmailChangeResult = 'success' | 'failure' | 'expired' | null;

export interface IProfileManagerProps {
	onClose: () => void;
	onLogout: () => void;
	onDeleteAccount: () => void;
	onEdit: (data: personalDetails) => Promise<any>;
	showGreeting?: boolean;
	subscription?: TSubscription;
	defaultPaymentMethodType?: TPaymentType;
	paymentScreen?: (
		body: Pick<DTicketProfileManagerProps, 'changeStep'>
	) => ReactNode;
	getDTicketManager?: (body: DTicketProfileManagerProps) => ReactNode;
	showLiveUpdatesVersion?: boolean;
	devFeatureFlagsScreen?: ReactNode;
	renderDevFeatureFlagsTrigger?: (onOpen: VoidFunction) => ReactNode;
}

const ProfileManagerComponent: FC<IProfileManagerProps> = ({
	onEdit,
	onClose,
	onLogout,
	onDeleteAccount,
	showGreeting,
	subscription,
	defaultPaymentMethodType,
	paymentScreen,
	getDTicketManager,
	showLiveUpdatesVersion = false,
	devFeatureFlagsScreen,
	renderDevFeatureFlagsTrigger,
}) => {
	const language = useI18nLanguage();
	const { t } = useTranslation(['profile', 'common', 'registration']);
	const { userData } = useUser();
	const { fetchUser } = useUserActions();
	const { isAboveMobile } = useBreakpoints();

	/** TODO Since we programmatically navigate between different screens, it should better be refactored to use a router and a queryparam instead. */
	const [profileStep, setProfileStep] = useState<ProfileSteps>(
		ProfileSteps.INITIAL
	);

	const devFeatureFlagsTriggerNode = renderDevFeatureFlagsTrigger?.(() =>
		setProfileStep(ProfileSteps.DEV_FEATURE_FLAGS)
	);

	const { replace } = useStatusBarStyleSync();

	useEffect(() => {
		replace({
			title: 'ProfileManager',
			style: Style.Dark,
		});
	}, [replace]);

	const { backInterceptor, setShowErrorModal, showErrorModal, isDriverApp } =
		useProfileManagerContext();

	const [showEditUserNotification, setShowEditUserNotification] =
		useState(false);

	const [invoiceAddressChanged, setInvoiceAddressChanged] =
		useState<boolean>(false);

	const [emailChangeModalType, setEmailChangeModalType] =
		useState<TEmailChangeResult>(null);

	const {
		confirmDiscardChanges: confirmDiscardInvoiceChanges,
		DiscardChangesConfirmationPrompt: DiscardInvoiceChanges,
	} = useDiscardChangesPrompt(() => {
		if (isAboveMobile) return setProfileStep(ProfileSteps.INITIAL);
		setProfileStep(ProfileSteps.DTICKET);
	}, invoiceAddressChanged);

	const location = useLocation();
	const query = useMemo(
		() => new URLSearchParams(location.search),
		[location.search]
	);

	const { subscriptionOffering } = useDTicketSubscriptionOffering();
	const dTicketType = getDTicketType(
		subscription?.type || subscriptionOffering?.subscriptionType
	);

	useEffect(() => {
		const subscriptionPauseStep = query.get('subscription');
		if (subscriptionPauseStep === 'pause') {
			setProfileStep(ProfileSteps.DTICKET_PAUSE);
		}

		const isPaymentDetails = query.has(EPassengerRouteParams.PaymentDetails);

		if (isPaymentDetails && !isAboveMobile) {
			setProfileStep(ProfileSteps.PAYMENT_DETAILS);
		}

		const changeEmailResult = query.get('changeEmail');
		if (changeEmailResult) {
			const generateModalForEmailChange = (
				changeEmailResult: TEmailChangeResult
			) => {
				window.history.replaceState({}, document.title, location.pathname);
				setProfileStep(ProfileSteps.PASSENGER_PERSONAL_DATA);
				switch (changeEmailResult) {
					case 'success':
						setEmailChangeModalType('success');
						break;
					case 'failure':
						setEmailChangeModalType('failure');
						break;
					case 'expired':
						setEmailChangeModalType('expired');
						break;
				}
			};

			generateModalForEmailChange(changeEmailResult as TEmailChangeResult);
		}
	}, [query, isAboveMobile, location.pathname]);

	const onPassengerEdited = () => {
		setProfileStep(ProfileSteps.PASSENGER_PERSONAL_DATA);
		fetchUser();
	};

	const { handleOpen: onTransportTypes } = useMoplaRideTypesControl();

	const onImprint: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		Browser.open({ url: 'https://www.mopla.solutions/de/impressum' });
	};

	const onCookieSettings: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		const windowRef = window as any;
		if (windowRef.CookieScript) {
			windowRef.CookieScript.instance.show();
		}
	};

	const onFAQ = () => setProfileStep(ProfileSteps.FAQ);

	const windowRef = window as any;

	const getDriverStep = () => {
		switch (profileStep) {
			case ProfileSteps.LANGUAGE_SELECT:
				return (
					<LanguageSelectScreen
						onSubmit={() => setProfileStep(ProfileSteps.INITIAL)}
					/>
				);

			case ProfileSteps.DRIVER_REVIEWS:
				return <DriverReviewPage />;

			case ProfileSteps.INITIAL:
			default:
				return (
					<LanguageSelectScreen
						onSubmit={() => setProfileStep(ProfileSteps.INITIAL)}
					/>
				);
		}
	};

	const personalScreenWithProps = (
		<PersonalScreen
			onDeleteAccount={onDeleteAccount}
			onEdit={() => setProfileStep(ProfileSteps.PASSENGER_EDIT_USER)}
			onEditEmail={() => setProfileStep(ProfileSteps.PASSENGER_EDIT_EMAIL)}
			currentUser={userData as IWhoami}
			onCloseModal={() => setEmailChangeModalType(null)}
			emailChangeModalType={emailChangeModalType}
		/>
	);

	const onAbortEdit = (isDirty: boolean) => {
		if (isDirty) {
			setShowEditUserNotification(true);
		} else {
			setProfileStep(ProfileSteps.PASSENGER_PERSONAL_DATA);
		}
	};

	const getPassengerStep = () => {
		switch (profileStep) {
			case ProfileSteps.FAQ:
				return <FAQScreen />;

			case ProfileSteps.LANGUAGE_SELECT:
				return (
					<LanguageSelectScreen
						onSubmit={() => setProfileStep(ProfileSteps.INITIAL)}
					/>
				);

			case ProfileSteps.PASSENGER_PERSONAL_DATA:
				return personalScreenWithProps;

			case ProfileSteps.PASSENGER_EDIT_USER:
				return userData ? (
					<EditUserScreen
						onUserEdited={onPassengerEdited}
						onEdit={onEdit}
						currentUser={userData}
						onAbort={onAbortEdit}
					/>
				) : (
					<Loader />
				);

			case ProfileSteps.PASSENGER_EDIT_EMAIL:
				return userData ? (
					<EditEmailScreen
						email={userData.email || ''}
						onAbort={onAbortEdit}
						setProfileStep={setProfileStep}
					/>
				) : (
					<Loader />
				);

			case ProfileSteps.PASSENGER_EDIT_EMAIL_SENT:
				return <VerifyEditEmailScreen />;

			case ProfileSteps.DEV_FEATURE_FLAGS:
				return devFeatureFlagsScreen ?? null;

			case ProfileSteps.PAYMENT_DETAILS:
				return (
					paymentScreen?.({
						changeStep: setProfileStep,
					}) || ''
				);

			case ProfileSteps.DTICKET:
			case ProfileSteps.DTICKET_PAUSE:
			case ProfileSteps.DTICKET_CANCEL:
			case ProfileSteps.DTICKET_INVOICE_ADDRESS:
			case ProfileSteps.DTICKET_INVOICE_INQUIRY:
			case ProfileSteps.DTICKET_INVOICE_INQUIRY_SELECT:
			case ProfileSteps.DTICKET_PREORDERED_CANCEL:
				return getDTicketManager?.({
					step: profileStep,
					changeStep: setProfileStep,
					setInvoiceAddressChanged: setInvoiceAddressChanged,
				});

			default:
				return (
					<LanguageSelectScreen
						onSubmit={() => setProfileStep(ProfileSteps.INITIAL)}
					/>
				);
		}
	};

	const getTitle = () => {
		switch (profileStep) {
			case ProfileSteps.INITIAL: {
				return `${userData?.firstName || ' '} ${userData?.lastName || ' '}`;
			}
			case ProfileSteps.LANGUAGE_SELECT:
				return t('header.language');
			case ProfileSteps.DRIVER_REVIEWS:
				return t('feedback.all_reviews');
			case ProfileSteps.PASSENGER_PERSONAL_DATA:
				return t('header.my_details');
			case ProfileSteps.PASSENGER_EDIT_USER:
				return t('header.personal_data');
			case ProfileSteps.PASSENGER_EDIT_EMAIL:
				return t('header.email');
			case ProfileSteps.PASSENGER_EDIT_EMAIL_SENT:
				return t('registration:text.header.success');
			case ProfileSteps.DRIVER_PERSONAL_DATA:
				return t('header.my_details');
			case ProfileSteps.PAYMENT_DETAILS:
				return t('header.payment');
			case ProfileSteps.DTICKET:
			case ProfileSteps.DTICKET_INVOICE_INQUIRY:
			case ProfileSteps.DTICKET_INVOICE_INQUIRY_SELECT:
				return t('header.germany_ticket', { dTicketType });
			case ProfileSteps.DTICKET_PAUSE:
				return t('header.pause_subscription');
			case ProfileSteps.DTICKET_CANCEL:
				return t('header.cancel_subscription');
			case ProfileSteps.DTICKET_INVOICE_ADDRESS:
				return t('header.change_billing_address');
			case ProfileSteps.DTICKET_PREORDERED_CANCEL:
				return t('header.cancel_preordered_subscription');
			case ProfileSteps.FAQ:
				return t('header.faq');
			case ProfileSteps.DEV_FEATURE_FLAGS:
				return 'Feature Flags';
			default:
				return t('header.initial');
		}
	};

	const getBackHandler = () => {
		if (backInterceptor) {
			return backInterceptor();
		}

		switch (profileStep) {
			case ProfileSteps.LANGUAGE_SELECT:
				return setProfileStep(ProfileSteps.INITIAL);
			case ProfileSteps.DEV_FEATURE_FLAGS:
				return setProfileStep(ProfileSteps.INITIAL);
			case ProfileSteps.PASSENGER_PERSONAL_DATA:
				return setProfileStep(ProfileSteps.INITIAL);
			case ProfileSteps.DRIVER_PERSONAL_DATA:
				return setProfileStep(ProfileSteps.INITIAL);
			case ProfileSteps.PAYMENT_DETAILS:
				return setProfileStep(ProfileSteps.INITIAL);
			case ProfileSteps.PASSENGER_EDIT_USER:
				return setShowEditUserNotification(true);
			case ProfileSteps.PASSENGER_EDIT_EMAIL:
				return setShowEditUserNotification(true);
			case ProfileSteps.PASSENGER_EDIT_EMAIL_SENT:
				return setProfileStep(ProfileSteps.PASSENGER_PERSONAL_DATA);
			case ProfileSteps.DTICKET:
				return setProfileStep(ProfileSteps.INITIAL);
			case ProfileSteps.DTICKET_CANCEL:
			case ProfileSteps.DTICKET_PAUSE:
			case ProfileSteps.DTICKET_PREORDERED_CANCEL:
				if (isAboveMobile) return setProfileStep(ProfileSteps.INITIAL);
				return setProfileStep(ProfileSteps.DTICKET);
			case ProfileSteps.DTICKET_INVOICE_ADDRESS:
				return confirmDiscardInvoiceChanges();
			case ProfileSteps.DTICKET_INVOICE_INQUIRY:
				if (isAboveMobile) {
					return setProfileStep(ProfileSteps.INITIAL);
				} else {
					return setProfileStep(ProfileSteps.DTICKET);
				}
			case ProfileSteps.DTICKET_INVOICE_INQUIRY_SELECT:
				return setProfileStep(ProfileSteps.DTICKET_INVOICE_INQUIRY);
			default:
				return setProfileStep(ProfileSteps.INITIAL);
		}
	};

	const closeUserEditorHandler = () => setShowEditUserNotification(false);

	const userEditorDiscardHandler = () => {
		setShowEditUserNotification(false);
		setProfileStep(ProfileSteps.PASSENGER_PERSONAL_DATA);
	};
	const driverProfile = (
		<DriverFeedbackProvider>
			<Root ownerState={{ variant: 'primary_light' }}>
				<MplPageHeader
					showBack={profileStep !== ProfileSteps.INITIAL}
					showClose
					open
					onClose={onClose}
					onBack={getBackHandler}
					title={getTitle()}
					variant="primaryDark"
					size="medium"
					color="neutral.0"
				/>
				<InitialProfileScreen
					onLogout={onLogout}
					onReviews={() => setProfileStep(ProfileSteps.DRIVER_REVIEWS)}
					onProfile={() => setProfileStep(ProfileSteps.DRIVER_PERSONAL_DATA)}
					onPayment={() => setProfileStep(ProfileSteps.PAYMENT_DETAILS)}
					onLanguage={() => setProfileStep(ProfileSteps.LANGUAGE_SELECT)}
					onTransportTypes={onTransportTypes}
					showGreeting={showGreeting}
					showLiveUpdatesVersion={showLiveUpdatesVersion}
				/>
				<BottomSlider
					open={profileStep !== ProfileSteps.INITIAL}
					onBack={getBackHandler}
					title={getTitle()}
				>
					{getDriverStep()}
				</BottomSlider>
			</Root>
		</DriverFeedbackProvider>
	);

	const mobilePassengerProfile = (
		<Root ownerState={{ variant: 'primary_dark' }}>
			{DiscardInvoiceChanges}
			<ProfileHeader
				open
				title={`${userData?.firstName || ' '} ${userData?.lastName || ' '}`}
			/>

			<InitialProfileScreen
				onLogout={onLogout}
				onProfile={() => setProfileStep(ProfileSteps.PASSENGER_PERSONAL_DATA)}
				onPayment={() => setProfileStep(ProfileSteps.PAYMENT_DETAILS)}
				onLanguage={() => setProfileStep(ProfileSteps.LANGUAGE_SELECT)}
				onTransportTypes={onTransportTypes}
				onFAQ={onFAQ}
				onImprint={onImprint}
				onCookieSettings={onCookieSettings}
				devFeatureFlagsTriggerNode={devFeatureFlagsTriggerNode}
				showGreeting={showGreeting}
				defaultPaymentMethodType={defaultPaymentMethodType}
				subscription={subscription}
				showLiveUpdatesVersion={showLiveUpdatesVersion}
			/>
			<BottomSlider
				open={profileStep !== ProfileSteps.INITIAL}
				onBack={getBackHandler}
				title={getTitle()}
			>
				{getPassengerStep()}
			</BottomSlider>
		</Root>
	);

	const desktopPassengerProfile = (
		<DesktopRoot>
			<DesktopDataBlock className="desktop-data-block">
				<DesktopDataTitle data-testid="desktopProfile-title">
					{t('header.my_details')}
				</DesktopDataTitle>
				{DiscardInvoiceChanges}
				{personalScreenWithProps}
				<MyAccountTitleWithButton>
					<Typography
						data-testid="desktopProfile-headerLanguageText"
						variant="bodyTitle"
						color="neutral.100"
					>
						{t('header.language')}
					</Typography>
					<IconButton
						data-testid="desktopProfile-headerLanguageButton"
						onClick={() => setProfileStep(ProfileSteps.LANGUAGE_SELECT)}
					>
						<EditIcon />
					</IconButton>
				</MyAccountTitleWithButton>
				<Typography color="neutral.100">
					{t(`profile:button.${language}`)}
				</Typography>
			</DesktopDataBlock>
			<DesktopDataBlock
				className="desktop-data-block"
				data-testid="desktopProfile-dTicketBlock"
			>
				<DesktopDataTitle data-testid="desktopProfile-dTicketTitle">
					{t('header.germany_ticket', { dTicketType })}
				</DesktopDataTitle>
				{getDTicketManager?.({
					step: profileStep,
					changeStep: setProfileStep,
					setInvoiceAddressChanged: setInvoiceAddressChanged,
					staticFirstStep: true,
				})}
			</DesktopDataBlock>
			{paymentScreen ? (
				<DesktopDataBlock
					className="desktop-data-block"
					data-testid="desktopProfile-paymentBlock"
				>
					<DesktopDataTitle data-testid="desktopProfile-paymentTitle">
						{t('header.payment')}
					</DesktopDataTitle>
					{paymentScreen?.({ changeStep: setProfileStep })}
				</DesktopDataBlock>
			) : null}
			<SideOverlay
				open={
					profileStep === ProfileSteps.LANGUAGE_SELECT ||
					profileStep === ProfileSteps.PASSENGER_EDIT_USER ||
					profileStep === ProfileSteps.PASSENGER_EDIT_EMAIL ||
					profileStep === ProfileSteps.PASSENGER_EDIT_EMAIL_SENT ||
					profileStep === ProfileSteps.DTICKET ||
					profileStep === ProfileSteps.DTICKET_PAUSE ||
					profileStep === ProfileSteps.DTICKET_CANCEL ||
					profileStep === ProfileSteps.DTICKET_INVOICE_ADDRESS ||
					profileStep === ProfileSteps.DTICKET_PREORDERED_CANCEL ||
					profileStep === ProfileSteps.DTICKET_INVOICE_INQUIRY ||
					profileStep === ProfileSteps.DTICKET_INVOICE_INQUIRY_SELECT ||
					profileStep === ProfileSteps.FAQ ||
					profileStep === ProfileSteps.DEV_FEATURE_FLAGS
				}
				title={getTitle()}
				onOpen={() => undefined}
				onClose={getBackHandler}
			>
				{getPassengerStep()}
			</SideOverlay>
			<ImprintButton testId="desktopProfile-faqButton" onClick={onFAQ}>
				<Typography variant="body2" color="primary">
					{t('profile:button.faq')}
				</Typography>
			</ImprintButton>
			<ImprintButton testId="desktopProfile-imprintButton" onClick={onImprint}>
				<Typography variant="body2" color="primary">
					{t('profile:button.imprint')}
				</Typography>
			</ImprintButton>
			{windowRef.CookieScript && (
				<ImprintButton
					testId="desktopProfile-cookieSettingsButton"
					onClick={onCookieSettings}
				>
					<Typography variant="body2" color="primary">
						{t('profile:button.cookie_settings')}
					</Typography>
				</ImprintButton>
			)}
			<ImprintButton
				testId="desktopProfile-transportTypesButton"
				onClick={onTransportTypes}
			>
				<Typography variant="body2" color="primary">
					{t('profile:button.transport_types')}
				</Typography>
			</ImprintButton>
			<MplButton
				fullWidth
				testId="desktopProfile-logOutButton"
				variant="outlined"
				onClick={onLogout}
				sx={{ textTransform: 'capitalize' }}
			>
				{t('profile:button.logout')}
			</MplButton>
			<MplButton
				testId="desktopProfile-deleteAccountButton"
				variant="text"
				color="error"
				onClick={onDeleteAccount}
				endIcon={<TrashIconSmall />}
				sx={{ justifySelf: 'center' }}
			>
				{t('button.delete_account')}
			</MplButton>
		</DesktopRoot>
	);

	if (isDriverApp) {
		return driverProfile;
	}

	return (
		<>
			<MplErrorModal
				open={showErrorModal}
				testId="profileManager-errorModal"
				buttons={
					<MplButton fullWidth onClick={() => setShowErrorModal(false)}>
						{t('errors:button.close')}
					</MplButton>
				}
			/>
			<ClosePersonalEditorModal
				onClose={closeUserEditorHandler}
				onDiscard={userEditorDiscardHandler}
				isOpen={showEditUserNotification}
			/>
			{isAboveMobile ? desktopPassengerProfile : mobilePassengerProfile}
		</>
	);
};

export const ProfileManager = withProfileManagerContext(
	ProfileManagerComponent
);
